/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {effect} from '@preact/signals-react';
import {warn} from '@ion/core/src/logger/logger';
import {GenericObject} from '@ion/core/src/types/entities';
import JobProgressObserver from '@ion/services/src/websocket/JobProgressObserver';
import {
  EntitySubscription,
  JobProgressUpdate,
  ObservableEntityId,
  SubscriptionResults
} from '@ion/services/src/websocket/types';
import {onSocketReconnected, onSubscriptionResult} from '@/store/actions/websocket';
import {liveGridActive} from './signals/liveUpdates';
import {diffSets} from './JobSearchEngineUtils';

const CONFIRMATION_TIMEOUT = 10000;

/**
 * A surface which has declared the job ids it wants observed, together with
 * the callbacks it is handed for them. The record is registered once and
 * mutated in place, so its callbacks are never stale and re-registering is
 * never necessary.
 */
export interface JobSubscriptionConsumer {
  ids: number[],
  onDelta?: (delta: JobProgressUpdate, jobId: number) => void,
  onResync?: () => void
}

const consumers = new Set<JobSubscriptionConsumer>();

let observed = new Set<number>();

const pendingRemovals = new Set<number>();

let inFlight = 0;

let confirmationTimer: ReturnType<typeof setTimeout> | null = null;

let disposeReconnect: (() => void) | null    = null;
let disposeSubscription: (() => void) | null = null;

let wasLiveGridActive: boolean | undefined;

/**
 * The single `jobProgress` observer for the whole application. Every
 * subscription routes through this module, so there is exactly one observer
 * identity, which is what stops one surface unmounting from emptying the
 * observer array for an id another surface is still rendering.
 *
 * `JobStatusSubscriptions.ts` makes the same guarantee, independently, for the
 * `jobStatus` channel - `Observers.addObserver` appends with no dedupe, so the
 * two must never share an identity or a registry.
 */
const onProgress = (change: GenericObject, id?: ObservableEntityId): void => {
  const jobId = Number(id);

  if (observed.has(jobId)) {
    consumers.forEach(consumer => {
      if (consumer.ids?.includes(jobId)) {
        consumer.onDelta?.(change as JobProgressUpdate, jobId);
      }
    });
  }
};

const clearConfirmationTimer = (): void => {
  if (confirmationTimer) {
    clearTimeout(confirmationTimer);
    confirmationTimer = null;
  }
};

/**
 * Sends the held-back unsubscribe, if there is one, and drops the ids from the
 * observed set. Called once nothing is left in flight, so an unsubscribe is
 * only ever on the wire while the subscribe that displaced it is answered.
 */
const flushRemovals = (): void => {
  clearConfirmationTimer();
  if (pendingRemovals.size) {
    const removals = Array.from(pendingRemovals);

    pendingRemovals.clear();
    removals.forEach(id => observed.delete(id));
    inFlight += 1;
    JobProgressObserver.removeObserver(removals, onProgress);
  }
};

/**
 * Counts one answered message, and releases the departures once the service has
 * answered everything we sent. Registered against the bootstrap's subscription
 * registry, so it sees every reply for every entity - hence the `jobProgress`
 * filter, without which a `jobStatus` reply would open this gate.
 */
const onSubscriptionAnswered = ({entity}: SubscriptionResults): void => {
  if (entity === EntitySubscription.jobProgress && inFlight) {
    inFlight -= 1;
    if (!inFlight) {
      flushRemovals();
    }
  }
};

/**
 * Recomputes the union of every registered consumer's ids and reconciles it
 * with what is currently observed, subscribing the arrivals immediately and
 * holding the departures back until the service has confirmed them.
 *
 * Diffing is a correctness requirement rather than an optimisation:
 * `Observers.addObserver` appends with no dedupe, so re-adding the same
 * observer for an already observed id makes every subsequent change for that
 * id invoke it twice.
 *
 * The departures wait because the two messages are otherwise sent in the same
 * tick, which leaves the service holding a subscribe and an unsubscribe for the
 * same entity at once and the client unable to tell the two replies apart. A
 * departing id stays observed while it waits, which costs a round trip of
 * changes for jobs no longer on screen - dropped by the `consumer.ids` guard in
 * `onProgress`, so nothing downstream sees them.
 */
export const sync = (): void => {
  const desired = new Set<number>();

  if (liveGridActive.value) {
    consumers.forEach(({ids}) => ids?.forEach(id => desired.add(id)));
  }

  const {added, removed} = diffSets(desired, observed);

  removed.forEach(id => pendingRemovals.add(id));
  desired.forEach(id => pendingRemovals.delete(id));

  const next = new Set<number>();

  desired.forEach(id => next.add(id));
  pendingRemovals.forEach(id => next.add(id));
  observed = next;

  if (added.length) {
    inFlight += 1;
    JobProgressObserver.addObserver(added, onProgress);
  }
  if (inFlight) {
    if (pendingRemovals.size && !confirmationTimer) {
      confirmationTimer = setTimeout(function() {
        warn('[ADM] jobProgress subscription was not answered within ' +
          `${CONFIRMATION_TIMEOUT}ms, unsubscribing the departed ids anyway`);
        inFlight = 0;
        flushRemovals();
      }, CONFIRMATION_TIMEOUT);
    }
  } else {
    flushRemovals();
  }
};

/**
 * Re-reconciles the union whenever the grid's live state changes.
 *
 * `sync()` is what carries both directions: switching off computes an empty
 * desired set and unsubscribes everything, switching on recomputes the union
 * and re-subscribes it. Without the off-side call nothing would leave the
 * observed set, and the `observed.has` guard in `onProgress` would keep
 * delivering deltas to a grid that is supposed to be still.
 *
 * The on-side additionally fans out `onResync()`, identical to what
 * `onSocketReconnected` does above - the grid's `onRefresh` and each open tab's
 * refetch - because the channel was quiet and the surfaces are behind.
 *
 * There is deliberately no catch-up query here. That is
 * `JobStatusSubscriptions`' reconnect action, and its channel never went quiet:
 * the toggle cannot reach it. Running one would re-ask for terminal events
 * which were already delivered and notified on.
 */
effect(function() {
  const active = liveGridActive.value;

  if (wasLiveGridActive !== undefined && wasLiveGridActive !== active) {
    sync();
    if (active) {
      consumers.forEach(consumer => consumer.onResync?.());
    }
  }
  wasLiveGridActive = active;
});

/**
 * Registers a consumer and returns its disposer. The reconnect fan-out is
 * attached on the first registration and released when the last consumer
 * leaves, so nothing is retained between visits to the route.
 *
 * Disposing removes the consumer's ids from the union and unsubscribes
 * whatever no other consumer still wants - which is where a job tab's
 * teardown comes from, with no code in the tab itself.
 */
export const register = (consumer: JobSubscriptionConsumer): (() => void) => {
  if (!disposeReconnect) {
    disposeReconnect = onSocketReconnected(function() {
      consumers.forEach(registered => registered.onResync?.());
    });
  }
  if (!disposeSubscription) {
    disposeSubscription = onSubscriptionResult(onSubscriptionAnswered);
  }
  consumers.add(consumer);

  return () => {
    consumers.delete(consumer);
    sync();
    if (!consumers.size) {
      disposeReconnect?.();
      disposeSubscription?.();
      disposeReconnect = null;
      disposeSubscription = null;
    }
  };
};

/**
 * Clears every consumer and unsubscribes every observed id. Module state
 * outlives a test case, so this exists for test isolation - never call it
 * from application code, which drops consumers through their own disposers.
 */
export const reset = (): void => {
  consumers.clear();
  wasLiveGridActive = liveGridActive.value;
  inFlight = 0;
  sync();
  clearConfirmationTimer();
  pendingRemovals.clear();
  observed = new Set<number>();
  inFlight = 0;
  disposeReconnect?.();
  disposeSubscription?.();
  disposeReconnect = null;
  disposeSubscription = null;
};
